/** Search box + result list for the food database (debounced, keyboard friendly). */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { errorMessage, log } from '../lib/logger';
import type { FoodSearchHit } from '../lib/types';
import { useApp } from '../state/app';
import { TrustBadge } from './Badges';
import { Icon } from './Icon';
import { Empty, Spinner } from './ui';

const g = (v: number | null): string => (v === null ? '—' : `${Math.round(v * 10) / 10} g`);

export function FoodSearch({ onPick, selectedId, autoFocus = false, mineOnly = false, refreshKey = 0, placeholder = 'Search foods and brands, e.g. "chicken breast" or "stouffers lasagna"' }: {
  onPick: (hit: FoodSearchHit) => void;
  selectedId?: number | null;
  autoFocus?: boolean;
  mineOnly?: boolean;
  refreshKey?: number;
  placeholder?: string;
}): ReactElement {
  const { source } = useApp();
  const [q, setQ] = useState<string>('');
  const [hits, setHits] = useState<FoodSearchHit[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<number>(-1);
  const reqId = useRef<number>(0);

  useEffect(() => {
    const term: string = q.trim();
    if (term.length < 2 && !mineOnly) { setHits([]); setError(null); return; }
    const id: number = ++reqId.current;
    setBusy(true);
    const t: ReturnType<typeof setTimeout> = setTimeout(() => {
      source.searchFoods(term, { limit: 40, mineOnly })
        .then((r) => { if (id === reqId.current) { setHits(r); setError(null); setCursor(-1); } })
        .catch((err: unknown) => { if (id === reqId.current) { setError(errorMessage(err)); log.warn('food_search.failed', { error: errorMessage(err) }); } })
        .finally(() => { if (id === reqId.current) setBusy(false); });
    }, 220);
    return () => clearTimeout(t);
  }, [q, source, mineOnly, refreshKey]);

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(hits.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === 'Enter') { const h = hits[cursor >= 0 ? cursor : 0]; if (h) onPick(h); }
  };

  const term: string = q.trim();
  return (
    <div className="food-search">
      <div className="search-box">
        <Icon name="search" size={18} />
        <input className="input" type="search" value={q} autoFocus={autoFocus} placeholder={mineOnly ? 'Filter my foods' : placeholder}
          onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} aria-label="Search foods" />
      </div>
      {error !== null && <div className="note err">{error}</div>}
      {busy && hits.length === 0 && <div className="row faint" style={{ fontSize: 13 }}><Spinner /> Searching…</div>}
      {!busy && error === null && term.length >= 2 && hits.length === 0 && (
        <Empty title="No matches">Try fewer or different words, or the brand and product (“totinos pizza rolls”). Packaged foods can also be scanned. You can also create your own food.</Empty>
      )}
      {!mineOnly && term.length < 2 && hits.length === 0 && (
        <p className="faint" style={{ fontSize: 13, margin: '4px 2px', fontWeight: 600 }}>Type at least 2 letters. Typos are OK: “chiken brest” works.</p>
      )}
      {mineOnly && !busy && hits.length === 0 && error === null && <Empty title="No foods of your own yet">Use “New food” to add one.</Empty>}
      <div className="hits" role="listbox" aria-label="Search results">
        {hits.map((h, i) => (
          <button key={h.id} type="button" role="option" aria-selected={selectedId === h.id}
            className={`hit ${selectedId === h.id || cursor === i ? 'on' : ''}`} onClick={() => onPick(h)}>
            <span className="hn">{h.name}{h.brand ? <span className="faint"> · {h.brand}</span> : null}</span>
            <span className="hk num">{h.energy_kcal !== null ? Math.round(h.energy_kcal) : '—'}<small> kcal</small></span>
            <span className="hm">
              <TrustBadge source={h.source} count={h.nutrient_count} />
              <span className="num">Protein {g(h.protein_g)} · Carbs {g(h.carbs_g)} · Fat {g(h.fat_g)} <span className="faint">per 100 g</span></span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
